/** Audio preferences live outside ParkState / save so economy saves stay untouched. */
export const SOUND_MUTE_STORAGE_KEY = 'park_v1_sound_muted';
export const AUDIO_CATEGORY_STORAGE_KEY = 'park_v1_audio_categories';

export type AudioCategoryId = 'sfx' | 'ambience' | 'music';

export type AudioCategoryState = {
  sfx: boolean;
  ambience: boolean;
  music: boolean;
};

const DEFAULT_CATEGORIES: AudioCategoryState = {
  sfx: true,
  ambience: true,
  music: true,
};

let memoryMuted = false;
let platformMuted = false;
let memoryCategories: AudioCategoryState = { ...DEFAULT_CATEGORIES };
const listeners = new Set<() => void>();

function readStoredMute(): boolean | null {
  try {
    const raw = globalThis.localStorage?.getItem(SOUND_MUTE_STORAGE_KEY);
    if (raw === '1') {
      return true;
    }
    if (raw === '0') {
      return false;
    }
  } catch {
    // Private mode / missing localStorage.
  }
  return null;
}

function readStoredCategories(): AudioCategoryState | null {
  try {
    const raw = globalThis.localStorage?.getItem(AUDIO_CATEGORY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AudioCategoryState>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return {
      sfx: typeof parsed.sfx === 'boolean' ? parsed.sfx : DEFAULT_CATEGORIES.sfx,
      ambience: typeof parsed.ambience === 'boolean' ? parsed.ambience : DEFAULT_CATEGORIES.ambience,
      music: typeof parsed.music === 'boolean' ? parsed.music : DEFAULT_CATEGORIES.music,
    };
  } catch {
    // Private mode / missing or corrupt localStorage.
  }
  return null;
}

function writeStoredMute(muted: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOUND_MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    // Preference stays in memory for this session.
  }
}

function writeStoredCategories(categories: AudioCategoryState): void {
  try {
    globalThis.localStorage?.setItem(AUDIO_CATEGORY_STORAGE_KEY, JSON.stringify(categories));
  } catch {
    // Preference stays in memory for this session.
  }
}

function notifyAudioSettings(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeAudioSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isSoundMuted(): boolean {
  return readStoredMute() ?? memoryMuted;
}

export function isPlatformMuted(): boolean {
  return platformMuted;
}

export function setPlatformMuted(muted: boolean): void {
  if (platformMuted === muted) {
    return;
  }
  platformMuted = muted;
  notifyAudioSettings();
}

export function isEffectivelyMuted(): boolean {
  return isSoundMuted() || platformMuted;
}

export function setSoundMuted(muted: boolean): void {
  memoryMuted = muted;
  writeStoredMute(muted);
  notifyAudioSettings();
}

export function getAudioCategories(): AudioCategoryState {
  return { ...(readStoredCategories() ?? memoryCategories) };
}

export function isAudioCategoryEnabled(category: AudioCategoryId): boolean {
  return getAudioCategories()[category];
}

export function setAudioCategoryEnabled(category: AudioCategoryId, enabled: boolean): void {
  const next = getAudioCategories();
  next[category] = enabled;
  memoryCategories = next;
  writeStoredCategories(next);
  notifyAudioSettings();
}

export function canPlaySfx(): boolean {
  return !isEffectivelyMuted() && isAudioCategoryEnabled('sfx');
}

export function canPlayAmbience(): boolean {
  return !isEffectivelyMuted() && isAudioCategoryEnabled('ambience');
}

export function canPlayMusic(): boolean {
  return !isEffectivelyMuted() && isAudioCategoryEnabled('music');
}

export function resetSoundSettingsForTests(): void {
  memoryMuted = false;
  platformMuted = false;
  memoryCategories = { ...DEFAULT_CATEGORIES };
  try {
    globalThis.localStorage?.removeItem(SOUND_MUTE_STORAGE_KEY);
    globalThis.localStorage?.removeItem(AUDIO_CATEGORY_STORAGE_KEY);
  } catch {
    // ignore
  }
}
